import { useDispatch } from "react-redux";
import { setFeedBackOpened } from "../../reduxSlices/feedback";
import { useEffect, useRef, useState } from "react";
import "../../components/feedback/feedback.css";
import { submitFeedback } from "../../serivces/feedbacksubmit";
import { useTranslation } from "react-i18next";

const Feedback = ({ color }) => {
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const { t } = useTranslation();
  const [loader, setLoader] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const feedbackTopRef = useRef(null);

  // Form state object to store all input values
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    feedbackMessage: "",
  });

  // Error state to track validation errors
  const [errors, setErrors] = useState({
    fullName: "",
    email: "",
    feedbackMessage: "",
  });

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    //  check word count limit
    if (name === "feedbackMessage") {
      const wordCount = value.trim() ? value.trim().split(/\s+/).length : 0;
      if (wordCount <= 200) {
        setFormData({ ...formData, [name]: value });
      }
    } else {
      // For other fields, update normally
      setFormData({ ...formData, [name]: value });
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {
      fullName: !formData.fullName.trim() ? "*Required" : "",
      email: !formData.email.trim()
        ? "*Required"
        : !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
              formData.email,
            )
          ? "Invalid email address"
          : "",
      feedbackMessage: !formData.feedbackMessage.trim() ? "*Required" : "",
    };

    setErrors(newErrors);

    // Return true if no errors
    return !Object.values(newErrors).some((error) => error);
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (validateForm()) {
      setLoader(true);
      try {
        const response = await submitFeedback({
          ...formData,
          file: selectedFile,
        });
        setFormData({
          fullName: "",
          email: "",
          phoneNumber: "",
          feedbackMessage: "",
        });
        setSelectedFile(null);
        setFileError(null);
        setErrors({
          fullName: "",
          email: "",
          feedbackMessage: "",
        });

        setLoader(false);
        setSubmissionError(false);
        setTimeout(() => setSubmissionError(null), 5000);
      } catch (error) {
        console.error("Error submitting feedback:", error);
        setLoader(false);
        setSubmissionError(true);
        setTimeout(() => setSubmissionError(null), 5000);
      }
    }
  };

  // useeffect for viewing the toaster after the submission error occurs
  useEffect(() => {
    if (submissionError !== null && feedbackTopRef.current) {
      feedbackTopRef.current.style.scrollMarginTop = "250px";
      feedbackTopRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [submissionError]);

  return (
    <div
      ref={feedbackTopRef}
      className="w-[95%] h-[80vh] mb-[0.63rem] bg-primary-background rounded-[0.6875rem] flex flex-col pt-[1.56rem] pl-[1.75rem] pr-[1.75rem] keyTabsContainer"
    >
      <div className="flex items-center relative">
        <button
          style={{ backgroundImage: `url(${"/feedback/back-icon.svg"})` }}
          className="w-[1.75rem] h-[1.75rem] cursor-pointer absolute left-0 hover:bg-setting-card active:bg-toggle-background bg-no-repeat bg-center bg-contain focus:outline-none focus:ring-2 focus:#3B4395 rounded-[50%]"
          alt="back"
          onClick={() => {
            dispatch(setFeedBackOpened());
          }}
        />
        <div className="text-text-black text-secondary font-quarternary leading-[1.375rem] w-full text-center">
          {t("Send feedback")}
        </div>
      </div>
      <div className="pb-[1.37rem] pt-[1.88rem]">
        {submissionError && (
          <div className="w-[100%] h-[2.375rem] bg-[#FAE5E5] flex items-center justify-start pl-[1.12rem] pr-[1.12rem] text-[0.875rem] font-[400] leading-[1.125rem] border border-file-error rounded-[0.25rem] mb-[1rem]">
            {t("Error submitting feedback. Please try again.")}
          </div>
        )}
        {!submissionError && submissionError != null && (
          <div className="w-[100%] h-[2.375rem] bg-[#C8EDF9] flex items-center justify-start pl-[1.12rem] pr-[1.12rem] text-[0.875rem] font-[400] leading-[1.125rem] border border-[#C8EDF9] rounded-[0.25rem] mb-[1rem]">
            {t("Feedback submitted successfully")}
          </div>
        )}
        <div className="flex flex-col gap-[0.5rem] ">
          <div className="mobileText flex items-center gap-[0.5rem] text-secondary font-tertiary leading-[1.375rem]">
            <div className="text-text-black">{t("Full name")}</div>
            {errors.fullName && (
              <div className="m-0 text-file-error text-quaternary font-primary leading-[1rem]">
                {t(errors.fullName)}
              </div>
            )}
          </div>
          <input
            name="fullName"
            value={formData.fullName}
            onChange={handleInputChange}
            placeholder={t("Enter your full name")}
            type="text"
            className={`w-[100%] text-text-black border ${errors.fullName ? "border-red-500" : "border-[0.063rem] focus-within:border-widget-background border-inner-widget-border"} rounded-[0.25rem] px-[1.12rem] py-[0.62rem] text-primary font-primary leading-[1.125rem] focus:outline-none placeholder:text-primary`}
          />
        </div>

        <div className="flex flex-col gap-[0.5rem] mt-[1rem]">
          <div className="mobileText flex items-center gap-[0.5rem] text-secondary font-tertiary leading-[1.375rem]">
            <div className="text-text-black">{t("Email")}</div>
            {errors.email && (
              <div className="m-0 text-file-error text-quaternary font-primary leading-[1rem]">
                {t(errors.email)}
              </div>
            )}
          </div>
          <input
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder={t("Enter your email address")}
            type="email"
            className={`w-[100%] text-text-black border ${errors.email ? "border-red-500" : "border-[0.063rem] focus-within:border-widget-background border-inner-widget-border"} rounded-[0.25rem] px-[1.12rem] py-[0.62rem] text-primary font-primary leading-[1.125rem] focus:outline-none placeholder:text-primary`}
          />
        </div>

        <div className="flex flex-col gap-[0.5rem] mt-[1rem]">
          <div className="mobileText flex items-center gap-[0.5rem] text-secondary font-tertiary leading-[1.375rem]">
            <div className="text-text-black">
              {t("Phone number (optional)")}
            </div>
          </div>
          <input
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleInputChange}
            placeholder={t("Enter your phone number")}
            type="text"
            className={`w-[100%] text-text-black border ${errors.phoneNumber ? "border-red-500" : "border-[0.063rem] focus-within:border-widget-background border-inner-widget-border"} rounded-[0.25rem] px-[1.12rem] py-[0.62rem] text-primary font-primary leading-[1.125rem] focus:outline-none placeholder:text-primary`}
          />
        </div>

        <div className="flex flex-col gap-[0.5rem] mt-[1rem]">
          <div className="mobileText text-secondary font-tertiary leading-[1.375rem] text-text-black">
            {t("Upload a photo (optional)")}
          </div>
          <div className="pr-[0.5rem] py-[0.25rem] relative w-full border border-[0.063rem] border-inner-widget-border rounded-[0.25rem] flex items-center overflow-hidden">
            <div
              className={`flex-grow py-[0.38rem] pl-[1.12rem]  text-primary font-primary leading-[1.125rem] truncate ${selectedFile ? "text-widget-background underline" : file - error ? "text-file-error" : "text-toggle-place-holder"}`}
            >
              {selectedFile
                ? selectedFile.name
                : file - error
                  ? t(file - error)
                  : t("Please attach a photo")}
            </div>

            {selectedFile ? (
              // Remove button when file is selected
              <button
                onClick={() => setSelectedFile(null)}
                style={{ background: color }}
                className="cursor-pointer px-[0.75rem] py-[0.85rem] font-primary text-primary rounded-[0.25rem] text-white h-full flex items-center"
              >
                {t("Remove")}
              </button>
            ) : (
              // Browse button when no file is selected
              <label
                className="cursor-pointer px-[0.75rem] font-primary text-primary rounded-[0.25rem] py-[0.85rem] text-white h-full flex items-center"
                tabIndex={0}
                style={{ background: color }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault(); // prevent page scroll on Space
                    e.currentTarget
                      .querySelector("input[type='file']")
                      ?.click(); // simulate file input click
                  }
                }}
              >
                {t("Browse file")}
                <input
                  type="file"
                  className="hidden"
                  accept=".jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      // Check file size (20MB = 20 * 1024 * 1024 bytes)
                      if (file.size > 20 * 1024 * 1024) {
                        setFileError(
                          "File size exceeds 20MB. Please try again",
                        );
                        return;
                      }

                      // Check file type
                      const validTypes = [
                        "image/jpeg",
                        "image/jpg",
                        "image/png",
                      ];
                      if (!validTypes.includes(file.type)) {
                        setFileError(
                          "Unsupported file format. Please try again",
                        );
                        return;
                      }

                      setSelectedFile(file);
                      setFileError(null);
                    }
                  }}
                />
              </label>
            )}
          </div>
          <div className="mobileDescription text-toggle-place-holder text-primary font-primary leading-[1.125rem]">
            {t("Max 20 MB limit. Supported formats: jpg, png, jpeg.")}
          </div>
        </div>

        <div className="flex flex-col gap-[0.5rem] mt-[1rem]">
          <div className="mobileText flex items-center gap-[0.5rem] text-secondary font-tertiary leading-[1.375rem]">
            <div className="text-text-black">{t("Add a feedback")}</div>
            {errors.feedbackMessage && (
              <div className="m-0 text-file-error text-quaternary font-primary leading-[1rem]">
                {t(errors.feedbackMessage)}
              </div>
            )}
          </div>
          <textarea
            name="feedbackMessage"
            value={formData.feedbackMessage}
            onChange={handleInputChange}
            // maxLength='200'
            placeholder={t("Describe your issue or share your suggestions")}
            className={`text-text-black min-h-[6.875rem] w-[100%] border ${errors.feedbackMessage ? "border-red-500" : "border-[0.063rem] focus-within:border-widget-background border-inner-widget-border"} rounded-[0.25rem] px-[1.12rem] py-[0.62rem] text-primary font-primary leading-[1.125rem] focus:outline-none placeholder:text-primary resize-none keytab`}
            style={{ height: "auto" }}
          />
        </div>

        <div className="mt-[0.38rem] mobileDescription text-toggle-place-holder text-primary font-primary leading-[1.125rem]">
          {t("Max words: ")}{" "}
          {formData.feedbackMessage.trim()
            ? formData.feedbackMessage.trim().split(/\s+/).length
            : 0}
          /200
        </div>
        {loader && (
          <div
            className="mt-[1rem] w-[100%]  py-[1.44rem] rounded-[0.5rem] feedBackSubmissionButton text-white text-secondary cursor-pointer text-nowrap text-center focus:outline-none focus:ring-2 focus:#3B4395 h-[0.5rem]  bg-[blue]  animate-pulse"
            style={{ background: color }}
          >
            {t("Send feedback")}
          </div>
        )}
        {!loader && (
          <button
            onClick={handleSubmit}
            className="mt-[1rem] w-[100%]  py-[1.44rem] rounded-[0.5rem] feedBackSubmissionButton text-white text-secondary cursor-pointer text-nowrap text-center focus:outline-none focus:ring-2 focus:#3B4395"
            style={{ background: color }}
          >
            {t("Send feedback")}
          </button>
        )}
      </div>
    </div>
  );
};

export default Feedback;

